import { PageLoader } from "./components/LoadingOverlay";
import ReportPreviewDialog from "./components/ReportPreviewDialog";
import { useReportPreview } from "./hooks/useReportPreview";
import { useSortAlpha } from "./hooks/useSortAlpha";
import { isReportNameKnown } from "./utils/reportBuilderUtils";
import {
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
  Undo as UndoIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as reportsV3Service from "services/reportsV3Service";

// ─── row components ───────────────────────────────────────────────────────────

const AvailableRow = ({ report, onAdd, onPreview, onDragStart, onDragEnd }) => {
  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStart("available", report.internalName, null);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onAdd(report)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        cursor: "pointer",
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Typography
        variant={"body2"}
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={report.displayName || report.internalName}
      >
        {report.displayName || report.internalName}
      </Typography>
      {report.currentOnly && (
        <Chip
          label={"Current only"}
          size={"small"}
          variant={"outlined"}
          sx={{
            flexShrink: 0,
            fontSize: "0.7rem",
            height: 20,
            pointerEvents: "none",
          }}
        />
      )}
      <Tooltip title={"Preview data"}>
        <IconButton
          size={"small"}
          onClick={(e) => {
            e.stopPropagation();
            onPreview(
              report.internalName,
              report.displayName || report.internalName
            );
          }}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <VisibilityIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const SelectedRow = ({
  report,
  index,
  onRemove,
  onRename,
  onRevert,
  onToggleVersion,
  onPreview,
  onDragStart,
  onDragOver,
  onDragEnd,
  disabled,
}) => {
  const nameChanged = report.displayName !== report.originalDisplayName;

  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStart("selected", report.internalName, index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        onDragOver(e.clientY < rect.top + rect.height / 2 ? index : index + 1);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onRemove(report.internalName)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        cursor: "pointer",
        backgroundColor: "primary.50",
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
        "&:hover": { backgroundColor: "primary.100" },
      }}
    >
      <DragIndicatorIcon
        sx={{
          fontSize: "1rem",
          color: "text.disabled",
          flexShrink: 0,
          cursor: "grab",
        }}
        onDragStart={(e) => e.stopPropagation()} // Let row's dragStart fire instead
      />
      {/* Inline editable name — click stops propagation so row-click doesn't fire */}
      <TextField
        variant={"standard"}
        value={report.displayName}
        onChange={(e) => onRename(report.internalName, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => e.stopPropagation()}
        disabled={disabled}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-input": {
            py: 0,
            fontWeight: 500,
            fontSize: "0.875rem",
            cursor: "text",
          },
        }}
        slotProps={{ input: { disableUnderline: !nameChanged } }}
      />
      {nameChanged && (
        <Tooltip title={"Revert to original name"}>
          <IconButton
            size={"small"}
            onClick={(e) => {
              e.stopPropagation();
              onRevert(report.internalName);
            }}
            sx={{ p: 0.25, flexShrink: 0 }}
            disabled={disabled}
          >
            <UndoIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
      )}
      {report.versionMode === "current_only" && (
        <Chip
          label={"Current only"}
          size={"small"}
          variant={"outlined"}
          sx={{
            flexShrink: 0,
            fontSize: "0.7rem",
            height: 20,
            pointerEvents: "none",
          }}
        />
      )}
      {(report.versionMode === "latest" ||
        report.versionMode === "current") && (
        <Tooltip
          title={
            report.versionMode === "latest"
              ? "Using latest live version — click to use embedded version"
              : "Using embedded version — click to use latest live version"
          }
        >
          <Chip
            label={report.versionMode === "latest" ? "Latest" : "Current"}
            size={"small"}
            color={report.versionMode === "latest" ? "primary" : "default"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleVersion(report.internalName);
            }}
            sx={{
              flexShrink: 0,
              cursor: "pointer",
              fontSize: "0.7rem",
              height: 20,
            }}
            disabled={disabled}
          />
        </Tooltip>
      )}
      <Tooltip title={"Preview data"}>
        <IconButton
          size={"small"}
          onClick={(e) => {
            e.stopPropagation();
            onPreview(report.internalName, report.displayName);
          }}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <VisibilityIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const MultiTableReportEditor = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(reportId);

  const [sortAlpha] = useSortAlpha();
  const [reportName, setReportName] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedReports, setSelectedReports] = useState([]);
  const [originalReport, setOriginalReport] = useState(null);
  const [currentOnlyAvailable, setCurrentOnlyAvailable] = useState([]);
  const [error, setError] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  // Drag state — use a ref so drag handlers always see current values without
  // stale closures; we also mirror source into state for the Available highlight.
  const dragSourceRef = useRef(null);
  const [dropBeforeIndex, setDropBeforeIndex] = useState(null);
  const [isDragOverAvailable, setIsDragOverAvailable] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const { data: savedReports } = reportsV3Service.useGetSavedReportsQuery();
  const [createReport] = reportsV3Service.useCreateReportMutation();
  const [updateReport] = reportsV3Service.useUpdateReportMutation();
  const [fetchReport] = reportsV3Service.useLazyGetReportQuery();
  const [
    fetchColumnsForReport,
  ] = reportsV3Service.useLazyGetColumnsForReportQuery();

  const {
    previewOpen,
    previewTitle,
    previewData,
    previewLoading,
    previewError,
    handlePreviewReport,
    closePreview,
  } = useReportPreview();

  useEffect(() => {
    setReportName("");
    setReportDescription("");
    setAvailableSearch("");
    setSelectedReports([]);
    setOriginalReport(null);
    setCurrentOnlyAvailable([]);
    setError(null);

    if (!reportId) return;

    setIsLoadingReport(true);
    fetchReport(reportId)
      .unwrap()
      .then((report) => {
        setReportName(report.displayName || "");
        setReportDescription(report.description || "");
        setOriginalReport(report);
        const entries = (report.reports || [])
          .filter((r) => !r.notForOutput)
          .map((r) => {
            const canonical = (savedReports || []).find(
              (sr) => sr.internalName === r.internalName
            );
            const hasLatest = Boolean(canonical);
            return {
              internalName: r.internalName,
              displayName: r.displayName,
              originalDisplayName: canonical?.displayName || r.internalName,
              versionMode: hasLatest ? "current" : "current_only",
            };
          });
        setSelectedReports(entries);
      })
      .catch((e) => {
        setError(e?.data || e?.message || "Failed to load report");
      })
      .finally(() => setIsLoadingReport(false));
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedInternalNames = useMemo(
    () => new Set(selectedReports.map((r) => r.internalName)),
    [selectedReports]
  );

  const availableReports = useMemo(() => {
    const fromSaved = (savedReports || [])
      .filter(
        (r) =>
          !r.multiOutputReport && !selectedInternalNames.has(r.internalName)
      )
      .map((r) => ({ ...r, currentOnly: false }));
    const fromCurrentOnly = currentOnlyAvailable
      .filter((r) => !selectedInternalNames.has(r.internalName))
      .map((r) => ({ ...r, currentOnly: true }));
    const combined = sortAlpha
      ? [...fromSaved, ...fromCurrentOnly].sort((a, b) =>
          (a.displayName || a.internalName).localeCompare(
            b.displayName || b.internalName
          )
        )
      : [...fromSaved, ...fromCurrentOnly].sort(
          (a, b) =>
            new Date(b.updated ?? 0).getTime() -
            new Date(a.updated ?? 0).getTime()
        );
    if (!availableSearch.trim()) return combined;
    const lower = availableSearch.toLowerCase();
    return combined.filter((r) =>
      (r.displayName || r.internalName).toLowerCase().includes(lower)
    );
  }, [
    savedReports,
    selectedInternalNames,
    availableSearch,
    currentOnlyAvailable,
    sortAlpha,
  ]);

  const isNameKnown = useMemo(() => {
    const reportsToCheck = isEditing
      ? (savedReports || []).filter((r) => r.internalName !== reportId)
      : savedReports;
    return isReportNameKnown(reportName, reportsToCheck);
  }, [reportName, savedReports, isEditing, reportId]);

  const isBusy = isSaving || isLoadingReport;
  const canSave =
    !!reportName.trim() &&
    selectedReports.length > 0 &&
    !isNameKnown &&
    !isBusy;

  // ── list mutations ──────────────────────────────────────────────────────────

  const handleAddReport = useCallback(
    (report, atIndex) => {
      const name = report.displayName || report.internalName;
      const entry = {
        internalName: report.internalName,
        displayName: name,
        originalDisplayName: name,
        /*
          versionMode takes on one of three values here:
          - current_only means thta only the currently embedded report is available.
          - current means that the embedded version is selected.
          - undefined mns in the following code as a default option
          which means that selection is not defined.
         */
        versionMode: report.currentOnly
          ? "current_only"
          : isEditing &&
            (originalReport?.reports || []).some(
              (e) => e.internalName === report.internalName
            )
          ? "current"
          : undefined,
      };
      if (report.currentOnly) {
        setCurrentOnlyAvailable((prev) =>
          prev.filter((r) => r.internalName !== report.internalName)
        );
      }
      setSelectedReports((prev) => {
        if (atIndex == null || atIndex >= prev.length) return [...prev, entry];
        const next = [...prev];
        next.splice(atIndex, 0, entry);
        return next;
      });
    },
    [isEditing, originalReport]
  );

  const handleRemoveReport = useCallback((internalName) => {
    setSelectedReports((prev) => {
      const entry = prev.find((r) => r.internalName === internalName);
      if (entry?.versionMode === "current_only") {
        setCurrentOnlyAvailable((av) => {
          if (av.some((r) => r.internalName === internalName)) return av;
          return [
            ...av,
            {
              internalName: entry.internalName,
              displayName: entry.displayName,
              currentOnly: true,
            },
          ];
        });
      }
      return prev.filter((r) => r.internalName !== internalName);
    });
  }, []);

  const handleMoveReport = useCallback((fromIndex, toIndex) => {
    setSelectedReports((prev) => {
      if (fromIndex === toIndex) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;
      next.splice(insertAt, 0, item);
      return next;
    });
  }, []);

  const handleRenameReport = useCallback((internalName, newName) => {
    setSelectedReports((prev) =>
      prev.map((r) =>
        r.internalName === internalName ? { ...r, displayName: newName } : r
      )
    );
  }, []);

  const handleRevertName = useCallback((internalName) => {
    setSelectedReports((prev) =>
      prev.map((r) =>
        r.internalName === internalName
          ? { ...r, displayName: r.originalDisplayName }
          : r
      )
    );
  }, []);

  const handleToggleVersion = useCallback((internalName) => {
    setSelectedReports((prev) =>
      prev.map((r) =>
        r.internalName === internalName &&
        (r.versionMode === "latest" || r.versionMode === "current")
          ? {
              ...r,
              versionMode: r.versionMode === "latest" ? "current" : "latest",
            }
          : r
      )
    );
  }, []);

  // ── drag handlers ───────────────────────────────────────────────────────────

  const handleDragStart = useCallback((source, internalName, index) => {
    dragSourceRef.current = { source, internalName, index };
  }, []);

  const handleDragEnd = useCallback(() => {
    dragSourceRef.current = null;
    setDropBeforeIndex(null);
    setIsDragOverAvailable(false);
  }, []);

  const handleDragOverSelected = useCallback(
    (e) => {
      // Fired on the container only when dragging over empty space (rows stopPropagation).
      e.preventDefault();
      setDropBeforeIndex(selectedReports.length);
    },
    [selectedReports.length]
  );

  const handleDropOnSelected = useCallback(
    (e) => {
      e.preventDefault();
      const src = dragSourceRef.current;
      if (!src) return;
      const insertAt = dropBeforeIndex ?? selectedReports.length;
      if (src.source === "available") {
        const report =
          (savedReports || []).find(
            (r) => r.internalName === src.internalName
          ) ||
          currentOnlyAvailable.find((r) => r.internalName === src.internalName);
        if (report) handleAddReport(report, insertAt);
      } else if (src.source === "selected") {
        handleMoveReport(src.index, insertAt);
      }
      setDropBeforeIndex(null);
    },
    [
      dropBeforeIndex,
      selectedReports.length,
      savedReports,
      currentOnlyAvailable,
      handleAddReport,
      handleMoveReport,
    ]
  );

  const handleDragOverAvailable = useCallback((e) => {
    if (dragSourceRef.current?.source === "selected") {
      e.preventDefault();
      setIsDragOverAvailable(true);
    }
  }, []);

  const handleDragLeaveAvailable = useCallback(() => {
    setIsDragOverAvailable(false);
  }, []);

  const handleDropOnAvailable = useCallback(
    (e) => {
      e.preventDefault();
      const src = dragSourceRef.current;
      if (src?.source === "selected") {
        handleRemoveReport(src.internalName);
      }
      setIsDragOverAvailable(false);
    },
    [handleRemoveReport]
  );

  // ── save / close ────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    setError(null);
    setIsSaving(true);
    try {
      // Fetch each selected report; if it is itself a MULTI_TABLE_REPORT,
      // splice in its child reports verbatim rather than wrapping it again.
      const nestedEntries = await Promise.all(
        selectedReports.map(async (r) => {
          // "Current" mode: use the version embedded in the original report.
          // NOTE: The embedded report needs to come after it's referenced reports.
          if (r.versionMode === "current" || r.versionMode === "current_only") {
            const embedded = (originalReport?.reports || []).find(
              (e) => e.internalName === r.internalName
            );
            if (embedded) {
              const result = [];
              if (embedded.requiredEmbeddedReports?.length) {
                const extras = embedded.requiredEmbeddedReports
                  .map((name) =>
                    (originalReport.reports || []).find(
                      (e) => e.internalName === name
                    )
                  )
                  .filter(Boolean);
                result.push(...extras);
              }
              return [...result, { ...embedded, displayName: r.displayName }];
            }
          }
          const report = await fetchReport(r.internalName).unwrap();
          if (report.type === "MULTI_TABLE_REPORT") {
            const children = report.reports || [];
            const notForOutputNames = children
              .filter((c) => c.notForOutput)
              .map((c) => c.internalName);
            return children.map((c) =>
              c.notForOutput || notForOutputNames.length === 0
                ? c
                : { ...c, requiredEmbeddedReports: notForOutputNames }
            );
          }
          const columns = await fetchColumnsForReport(
            r.internalName,
            true
          ).unwrap();
          return [
            {
              displayName: r.displayName,
              description: report.description ?? "",
              internalName: r.internalName,
              type: report.type,
              specification: report.specification,
              notForOutput: false,
              expectedColumns: columns.map((col) => col.original),
            },
          ];
        })
      );

      // Flatten and deduplicate — first occurrence wins.
      const seen = new Set();
      const reports = nestedEntries.flat().filter((entry) => {
        if (seen.has(entry.internalName)) return false;
        seen.add(entry.internalName);
        return true;
      });

      const reportData = {
        type: "MULTI_TABLE_REPORT",
        displayName: reportName.trim(),
        description: reportDescription.trim(),
        reports,
      };

      let savedId;
      if (isEditing) {
        await updateReport({ reportId, reportData }).unwrap();
        savedId = reportId;
      } else {
        const result = await createReport(reportData).unwrap();
        savedId = result?.id;
      }

      navigate(`/report-builder/${savedId}`);
    } catch (e) {
      setError(
        e?.data ||
          e?.message ||
          `Failed to ${isEditing ? "save" : "create"} report`
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    reportName,
    reportDescription,
    selectedReports,
    originalReport,
    isEditing,
    reportId,
    createReport,
    updateReport,
    fetchReport,
    fetchColumnsForReport,
    navigate,
  ]);

  const handleCancel = useCallback(() => {
    if (isBusy) return;
    if (isEditing) {
      navigate(`/report-builder/${reportId}`);
    } else {
      navigate("/report-builder");
    }
  }, [isBusy, isEditing, navigate, reportId]);

  // ── render ──────────────────────────────────────────────────────────────────

  if (isLoadingReport) {
    return <PageLoader />;
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Stack
          spacing={3}
          sx={{ flex: 1, overflow: "auto", p: 3, width: "100%" }}
        >
          <Paper sx={{ p: 2 }}>
            <Typography variant={"h6"} sx={{ mb: 2 }}>
              {isEditing
                ? "Edit Multi-Table Report"
                : "Create Multi-Table Report"}
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              flexShrink={0}
            >
              <TextField
                label={"Report Name"}
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 240 }}
                required
                error={isNameKnown}
                helperText={
                  isNameKnown ? "A report with this name already exists" : ""
                }
                disabled={isBusy}
                autoFocus
              />
              <TextField
                label={"Description"}
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                sx={{ flexGrow: 2 }}
                multiline
                maxRows={2}
                disabled={isBusy}
              />
            </Stack>
          </Paper>

          {error && (
            <Alert severity={"error"} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              height: "calc(100vh - 400px)",
              minHeight: 300,
            }}
          >
            {/* ── Available Reports ── */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                border: "1px solid",
                borderColor: isDragOverAvailable ? "primary.main" : "divider",
                borderRadius: 1,
                overflow: "hidden",
                transition: "border-color 0.15s",
                backgroundColor: "background.paper",
              }}
              onDragOver={handleDragOverAvailable}
              onDragLeave={handleDragLeaveAvailable}
              onDrop={handleDropOnAvailable}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  backgroundColor: "grey.50",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ fontWeight: 600, letterSpacing: 0.5 }}
                >
                  {"— Available Reports —"}
                </Typography>
                <TextField
                  size={"small"}
                  placeholder={"Search..."}
                  value={availableSearch}
                  onChange={(e) => setAvailableSearch(e.target.value)}
                  fullWidth
                  sx={{ mt: 0.75 }}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {availableReports.map((report) => (
                  <AvailableRow
                    key={report.internalName}
                    report={report}
                    onAdd={handleAddReport}
                    onPreview={handlePreviewReport}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                ))}
                {availableReports.length === 0 && (
                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    {"No reports found"}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* ── Selected Reports ── */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "background.paper",
              }}
              onDragOver={handleDragOverSelected}
              onDrop={handleDropOnSelected}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  backgroundColor: "grey.50",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ fontWeight: 600, letterSpacing: 0.5 }}
                >
                  {"— Selected Reports —"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {selectedReports.map((report, index) => (
                  <React.Fragment key={report.internalName}>
                    {dropBeforeIndex === index && (
                      <Box sx={{ height: 2, bgcolor: "primary.main", mx: 1 }} />
                    )}
                    <SelectedRow
                      report={report}
                      index={index}
                      onRemove={handleRemoveReport}
                      onRename={handleRenameReport}
                      onRevert={handleRevertName}
                      onToggleVersion={handleToggleVersion}
                      onPreview={handlePreviewReport}
                      onDragStart={handleDragStart}
                      onDragOver={setDropBeforeIndex}
                      onDragEnd={handleDragEnd}
                      disabled={isBusy}
                    />
                  </React.Fragment>
                ))}
                {dropBeforeIndex === selectedReports.length &&
                  selectedReports.length > 0 && (
                    <Box sx={{ height: 2, bgcolor: "primary.main", mx: 1 }} />
                  )}
                {selectedReports.length === 0 && (
                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    {"No reports selected"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
            <Button
              onClick={handleCancel}
              color={"secondary"}
              isDisabled={isBusy}
            >
              {"Cancel"}
            </Button>
            <Button
              onClick={handleSave}
              startIcon={<AddIcon />}
              isLoading={isSaving}
              isDisabled={!canSave}
            >
              {isEditing ? "Save Report" : "Create Report"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <ReportPreviewDialog
        open={previewOpen}
        onClose={closePreview}
        title={previewTitle}
        data={previewData}
        loading={previewLoading}
        error={previewError}
      />
    </>
  );
};

export default MultiTableReportEditor;
